import { useRef, useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import { ArrowRight, Check, RefreshCw05, Lock01, FileCheck02 } from "@untitledui/icons"
import { toast } from "sonner"
import { cx } from "@/lib/utils/cx"
import { ApiError } from "@/lib/api/client"
import { activationApi, type ActivationView } from "@/lib/api/onboarding-activation"
import {
  PrimaryButton,
  StageHeader,
  Field,
  inputClass,
  ActionRow,
  focusRing,
  focusFirstInvalid,
  CornerMarks,
} from "./ui"

// Stage 4 — CCi's Data Authorization. The legal text is reproduced verbatim
// from the Zoho onboarding form and always fully available in a scrollable
// frame; a plain-words panel above it explains what's being agreed to (labeled
// as a summary — the frame below is what gets signed). Acceptance, then typing
// the legal name renders it as a signature over a dated signing line. Server
// stamps IP/UA/version/text-hash (ESIGN/UETA). Body paragraphs prefixed "## "
// render as bold sub-headings (e.g. "Authorization").
export function SignStep({
  token,
  data,
  onBack,
  onSigned,
}: {
  token: string
  data: ActivationView
  onBack: () => void
  onSigned: (next: ActivationView) => void
}) {
  const [accepted, setAccepted] = useState(false)
  const [name, setName] = useState("")
  const [tried, setTried] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const nameRef = useRef<HTMLInputElement>(null)

  const validName = name.trim().length >= 2

  const plainWords = [
    {
      icon: RefreshCw05,
      text: "CCi pulls your estimate & repair-order data daily — automatically, with no access to your network.",
    },
    {
      icon: Lock01,
      text: "Your data is secured, never sold, and used only for this program.",
    },
    {
      icon: FileCheck02,
      text: "You can withdraw your consent at any time, in writing.",
    },
  ]

  const today = new Date().toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  })

  const sign = async () => {
    if (!accepted || !validName) {
      setTried(true)
      focusFirstInvalid()
      return
    }
    if (submitting) return
    setSubmitting(true)
    try {
      onSigned(await activationApi.sign(token, { signerName: name.trim(), accepted: true }))
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Your signature could not be recorded.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <StageHeader
        stage="Step 4 of 5"
        title="Read and sign the data authorization"
        lede="Three things to know, then read the full terms, tick the box to accept, and type your name to sign."
      />

      {/* plain words — comprehension aid, clearly labeled as a summary */}
      <ul className="space-y-2">
        {plainWords.map(({ icon: Icon, text }) => (
          <li key={text} className="flex items-start gap-3 rounded-lg bg-[#F4F6F5] px-3.5 py-2.5">
            <span className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-lg bg-white text-emerald-600 ring-1 ring-[#DCE4DF]">
              <Icon className="size-3.5" aria-hidden />
            </span>
            <span className="text-[13px] leading-relaxed text-[#33413A]">{text}</span>
          </li>
        ))}
      </ul>

      {/* the terms — always fully present, scrollable like a paper contract */}
      <div className="mt-4 overflow-hidden rounded-xl border border-[#D5DDD8]">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-[#EDF1EE] bg-[#FAFCFA] px-5 py-2.5">
          <span className="text-[13px] font-bold text-[#0C2018]">
            {data.agreement.title} — Terms and Conditions
          </span>
          <span className="shrink-0 text-[11.5px] font-medium text-[#96A29A]">
            v{data.agreement.version} · effective{" "}
            {new Date(data.agreement.effectiveDate).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </span>
        </div>
        {/* the full text, top to bottom — no inner scrollbox */}
        <div className="space-y-3 px-5 py-4 text-[13.5px] leading-relaxed text-[#4A5A51]">
          {data.agreement.body.map((para, i) =>
            para.startsWith("## ") ? (
              <p key={i} className="pt-1 text-[13px] font-bold text-[#0C2018]">
                {para.slice(3)}
              </p>
            ) : (
              <p key={i}>{para}</p>
            ),
          )}
        </div>
      </div>

      {/* acceptance */}
      <button
        type="button"
        role="checkbox"
        aria-checked={accepted}
        aria-invalid={(tried && !accepted) || undefined}
        onClick={() => {
          setAccepted((v) => {
            const next = !v
            if (next) setTimeout(() => nameRef.current?.focus(), 60)
            return next
          })
        }}
        className={cx(
          "mt-4 flex w-full items-start gap-3 rounded-xl border px-4 py-3 text-left transition-all duration-150",
          focusRing,
          accepted
            ? "border-emerald-400 bg-emerald-50/60"
            : tried && !accepted
              ? "border-red-400 bg-red-50/40"
              : "border-[#DCE4DF] hover:border-emerald-300",
        )}
      >
        <span
          className={cx(
            "mt-0.5 grid size-[22px] shrink-0 place-items-center rounded-md border transition-all duration-150",
            accepted ? "border-emerald-600 bg-emerald-600 text-white" : "border-[#C6D2CB] bg-white",
          )}
        >
          {accepted && <Check className="size-3.5" strokeWidth={3} />}
        </span>
        <span className="text-[13.5px] leading-snug text-[#4A5A51]">
          I accept the Terms and Conditions on behalf of{" "}
          <span className="font-bold text-[#0C2018]">{data.shop.legalBusinessName}</span>, and I am
          authorized to sign.
        </span>
      </button>
      {tried && !accepted && (
        <p className="mt-1.5 text-[12.5px] font-medium text-red-600" role="alert">
          Tick the box to accept the terms
        </p>
      )}

      {/* the signing block — framed by the brand's viewfinder brackets */}
      <div className="relative mt-5 bg-[#FAFBFA] p-5">
        <CornerMarks size={14} />
        <Field
          label="Your full legal name"
          hint="Electronic signature · ESIGN Act"
          error={tried && !validName && "Type your full legal name to sign"}
        >
          <input
            ref={nameRef}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={!accepted}
            placeholder="Full legal name"
            autoComplete="name"
            aria-invalid={(tried && !validName) || undefined}
            className={cx(
              inputClass(Boolean(tried && !validName)),
              !accepted && "cursor-not-allowed bg-[#F2F5F3] text-zinc-400",
            )}
          />
        </Field>

        <div className="mt-4 border-b-2 border-[#C6D2CB] pb-1.5">
          <div className="flex min-h-[36px] items-end justify-between gap-4">
            <AnimatePresence>
              {validName && accepted ? (
                <motion.span
                  key="sig"
                  initial={{ opacity: 1, clipPath: "inset(-20% 100% -20% 0)" }}
                  animate={{ clipPath: "inset(-20% 0% -20% 0)" }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
                  className="text-[28px] leading-none text-[#0C2018]"
                  style={{ fontFamily: "'Snell Roundhand', 'Segoe Script', 'Brush Script MT', cursive" }}
                >
                  {name.trim()}
                </motion.span>
              ) : (
                <span key="ph" className="text-[13px] italic text-[#C6D2CB]">
                  Your signature appears here
                </span>
              )}
            </AnimatePresence>
            <span className="shrink-0 text-[11.5px] font-medium text-[#96A29A]">{today}</span>
          </div>
        </div>
        <p className="mt-1.5 text-[11px] text-[#9AA8A0]">
          Signed for {data.shop.legalBusinessName} · we record the version and time of signing
        </p>
      </div>

      <ActionRow back={onBack}>
        <PrimaryButton onClick={sign} loading={submitting}>
          Sign the authorization
          <ArrowRight className="size-4" />
        </PrimaryButton>
      </ActionRow>
    </div>
  )
}
